import React, {useCallback, useEffect} from 'react';
import {UseFormReturn, useWatch} from "react-hook-form";
import {FormOperacionCapitalMarkets, OperacionCapitalMarkets} from "../types/types";
import CardActions from "../../../components/CardActions";
import {Grid, Typography} from "@mui/material";
import {OperacionCapitalMarketsFields} from "../types/fields";
import {EntityWithIdAndDescription, EntityWithIdAndDescriptionFields, EntityWithIdFields} from "../../../../../types";
import {AsyncSelect, Button, ControlledTextField} from "../../../../../components/forms";
import {numberFormatter} from "../../../../../utils/formatters/numberFormatter";
import {TextField} from "src/lib/itaptec/components_forms/src";
import {w} from "@fullcalendar/core/internal-common";
import {OperacionCapitalMarketsViewDTO} from "../types/dto";
import {HttpCacheOperacion} from "../../../api/httpCacheOperacion";

type ExtendedEntityWithIdAndDescription = EntityWithIdAndDescription & { booleanValue: boolean }

type DatosOperacionProps = {
  formMethods: UseFormReturn<FormOperacionCapitalMarkets>
  readOnly?: boolean
  operacion?: OperacionCapitalMarketsViewDTO
  codSistemaPrincipal: number
  nuevo?: boolean
}

function SeccionCapitalMarketsDatosOperacion({
                                               formMethods,
                                               readOnly,
                                               operacion,
                                               codSistemaPrincipal,
                                               nuevo
                                             }: DatosOperacionProps) {

  const {control, reset, getValues, setValue} = formMethods

  const watchEmisorPreAprobado = useWatch({control: control, name: OperacionCapitalMarketsFields.EmisorPreAprobado, defaultValue: false})
  const watchUnderwriting = useWatch({control: control, name: OperacionCapitalMarketsFields.Underwriting, defaultValue: false})
  const watchSuperaLimitesUnderwriting = useWatch({control: control, name: OperacionCapitalMarketsFields.SuperaLimitesUnderwriting, defaultValue: 'No'})
  const watchLimites = useWatch({control: control, name: OperacionCapitalMarketsFields.Limites, defaultValue: undefined})


  const getSuperaLimitesTipos = useCallback(() => HttpCacheOperacion.getCapitalMarketsSuperaLimitesUnderwritingTipos(codSistemaPrincipal), [codSistemaPrincipal])

  const getOpciones = useCallback<() => Promise<ExtendedEntityWithIdAndDescription[]>>(() => {
    return Promise.resolve([
      {[EntityWithIdFields.Id]: 1, [EntityWithIdAndDescriptionFields.Description]: 'Si', booleanValue: true},
      {[EntityWithIdFields.Id]: 0, [EntityWithIdAndDescriptionFields.Description]: 'No', booleanValue: false},
    ])
  }, [])

  const booleanToText = useCallback((value?: boolean) => value === true ? 'Si' : value === false ? 'No' : ''
    , [])

  useEffect(() => {
    if (!watchEmisorPreAprobado) {
      if (watchUnderwriting) {
        setValue(OperacionCapitalMarketsFields.Underwriting, false)
      };
    }
  }, [watchEmisorPreAprobado, setValue]);

  useEffect(() => {
    if (!watchUnderwriting) {
      if (watchSuperaLimitesUnderwriting) setValue(OperacionCapitalMarketsFields.SuperaLimitesUnderwriting, 'No');
    }
  }, [watchUnderwriting, setValue]);

  useEffect(() => {
    if (watchSuperaLimitesUnderwriting !== 'Si') {
      if (watchLimites) {
        setValue(OperacionCapitalMarketsFields.Limites,  '')
      }
    }
  },[watchSuperaLimitesUnderwriting, setValue])

  useEffect(() => {
    if(watchLimites === '') {
      setValue(OperacionCapitalMarketsFields.Limites, null)
    }
  },[watchLimites, setValue])

  return (
    <CardActions title='Datos Operación' colapsable>

      <Grid container spacing={4}>
        {
          !nuevo &&
            <Grid item xs={12} md={3}>
                <Typography variant='body2'>Usuario Alta</Typography>
                <Typography>{`${operacion?.nombreUsuarioAlta} ${operacion?.apellidoUsuarioAlta}`}</Typography>
            </Grid>
        }
        <Grid item xs={12} md={3}>
          {
            readOnly ?
              <>
                <Typography variant='body2'>Sector Privado</Typography>
                <Typography>{`${booleanToText(operacion?.[OperacionCapitalMarketsFields.SectorPrivado])} `}</Typography>
              </> :
              <AsyncSelect
                loadOptions={getOpciones} control={control}
                label={'Sector Privado'}
                fullWidth
                name={OperacionCapitalMarketsFields.SectorPrivado}
                valueField={'booleanValue'}
                selectNotNulleable
                defaultValue={false}
                InputLabelProps={{
                    shrink: true
                }}
              />
          }
        </Grid>
        <Grid item xs={12} md={3}>
          {
            readOnly ?

              <>
                <Typography variant='body2'>Emisor Pre Aprobado</Typography>
                <Typography>{`${booleanToText(operacion?.[OperacionCapitalMarketsFields.EmisorPreAprobado])} `}</Typography>
              </>

              :
              <AsyncSelect
                loadOptions={getOpciones} control={control}
                label={'Emisor Pre Aprobado'}
                fullWidth
                name={OperacionCapitalMarketsFields.EmisorPreAprobado}
                valueField={'booleanValue'}
                selectNotNulleable
                defaultValue={false}
                InputLabelProps={{
                    shrink: true
                }}
              />

          }
        </Grid>

        <Grid item xs={12} md={3}>
          {
            readOnly ?
              <>
                <Typography variant='body2'>Underwriting</Typography>
                <Typography>{`${booleanToText(operacion?.[OperacionCapitalMarketsFields.Underwriting])} `}</Typography>
              </> :
              <AsyncSelect
                loadOptions={getOpciones} control={control}
                label={'Underwriting'}
                fullWidth
                name={OperacionCapitalMarketsFields.Underwriting}
                selectNotNulleable
                defaultValue={false}
                valueField={'booleanValue'}
                InputLabelProps={{
                    shrink: true
                }}
              />
          }
        </Grid>
        <Grid item xs={12} md={3}>
          {
            readOnly ?
              <>
                <Typography variant='body2'>Supera Límites Underwriting</Typography>
                <Typography>{operacion?.[OperacionCapitalMarketsFields.SuperaLimitesUnderwriting]}</Typography>
              </> :
              <AsyncSelect
                loadOptions={getSuperaLimitesTipos}
                control={control}
                fullWidth
                label={'Supera Límites Underwriting'}
                name={OperacionCapitalMarketsFields.SuperaLimitesUnderwriting}
                valueField={EntityWithIdAndDescriptionFields.Description}
                selectNotNulleable
                defaultValue={false}
                disabled={!(watchUnderwriting)}
                InputLabelProps={{
                    shrink: true
                }}
              />
          }
        </Grid>
        <Grid item xs={12} md={3}>
          {
            readOnly ?
              <>
                <Typography variant='body2'>Límites</Typography>
                <Typography>{operacion?.[OperacionCapitalMarketsFields.Limites] || '-'}</Typography>
              </> :
              <ControlledTextField
                control={control}
                label={'Límites'}
                name={OperacionCapitalMarketsFields.Limites}
                disabled={!watchUnderwriting || (watchSuperaLimitesUnderwriting !== 'Si')}
                defaultValue={watchLimites}
              />
          }
        </Grid>
      </Grid>
    </CardActions>
  );
}

export default SeccionCapitalMarketsDatosOperacion;